import type { AccountInfo } from '@azure/msal-browser';

/**
 * What token acquisition needs from MSAL, structurally — so the policy below can be exercised
 * without a browser, a DOM or React. `IPublicClientApplication` satisfies it; nothing here
 * imports it, which is the whole point.
 */
export interface TokenClient {
  acquireTokenSilent(request: { scopes: string[]; account: AccountInfo }): Promise<{ accessToken: string }>;
  acquireTokenRedirect(request: { scopes: string[]; account: AccountInfo }): Promise<void>;
}

/**
 * Set once a redirect has been started, and never cleared while the page lives.
 *
 * Several requests can fail their silent acquisition at the same moment — a screen that opens
 * with four queries is four failures — and only the first of them should try to navigate.
 * MSAL refuses a second concurrent interaction (`interaction_in_progress`), and that refusal
 * would surface as exactly the error screen this whole change exists to stop painting.
 *
 * Module scope rather than a ref, because the fact is about the *page*: the getter is rebuilt
 * per component, and every one of them is moot once the browser is leaving.
 */
let redirecting = false;

/** Test seam. The flag is page-lifetime state and there is no page between test cases. */
export function resetRedirectingForTests(): void {
  redirecting = false;
}

/**
 * A promise that never settles, returned once a redirect is under way.
 *
 * The caller asked for a token and is not going to get one, because the document is being
 * replaced. Throwing here is what the old code did and it is wrong twice over: React Query
 * latches the rejection and paints "Couldn't verify your access" over a page that is already
 * navigating, and the user is left looking at a failure screen that had in fact recovered.
 * Never settling leaves the caller in its loading state, which is the honest description of
 * "we are leaving".
 */
const navigating = <T>(): Promise<T> => new Promise<T>(() => {});

/**
 * Acquire an API access token, falling back to an interactive redirect when the silent path
 * cannot produce one.
 *
 * **The fallback fires on *any* silent failure, deliberately, and must stay that way.** It used
 * to be gated on `InteractionRequiredAuthError`, which sounds precise and left a hole big enough
 * to strand every long-lived session: Entra caps SPA refresh tokens at around 24 hours, after
 * which MSAL renews silently through a hidden iframe, third-party cookie policy blocks the
 * iframe's session cookie, and MSAL reports a **`BrowserAuthError`** — `monitor_window_timeout`
 * — rather than an interaction-required one. The gate never matched, the fallback never ran, and
 * the app sat on an error card until somebody reloaded it by hand.
 *
 * Enumerating the error types that mean "ask the user" is what created that hole and is how the
 * next one gets created: the list is Microsoft's to change and ours to discover afterwards, in
 * production, hours into somebody's session. A silent acquisition that failed *is* the case where
 * interaction is needed — that is what "silent" means — so the condition is the failure itself.
 */
export async function acquireToken(client: TokenClient, account: AccountInfo, scope: string): Promise<string> {
  try {
    const result = await client.acquireTokenSilent({ scopes: [scope], account });
    return result.accessToken;
  } catch {
    if (redirecting) return navigating();
    redirecting = true;

    try {
      await client.acquireTokenRedirect({ scopes: [scope], account });
    } catch (redirectError) {
      // No navigation is coming, so this is a real failure and the caller should hear about it.
      // Clearing the flag matters: a refused interaction must not wedge the page into silently
      // hanging on every subsequent request.
      redirecting = false;
      throw redirectError;
    }

    return navigating();
  }
}
